import { useEffect, useState } from 'react';
import { MultiSelect } from '../shared';

const derivePreloadMode = (value) => {
    if (value === null || value === undefined) return 'all';
    if (Array.isArray(value) && value.length === 0) return 'none';
    return 'specific';
};

const deriveMentionMode = (value) => {
    if (value === null || value === undefined) return 'hide';
    if (value === 'all') return 'all';
    if (Array.isArray(value) && value.length === 0) return 'hide';
    return 'specific';
};


export default function PreloadBlock({
    preload,
    onPreloadChange,
    mention,
    onMentionChange,
    options,
    include = [],
    exclude = [],
    includeTags = [],
    excludeTags = [],
    loading,
    resourceLabel = 'resource',
    readTool = 'read_file',
    listPath = '/skills/',
    groupName,
}) {
    const [preloadMode, setPreloadMode] = useState(() => derivePreloadMode(preload));
    const [mentionMode, setMentionMode] = useState(() => deriveMentionMode(mention));

    useEffect(() => {
        const d = derivePreloadMode(preload);
        if (d !== 'none' || preloadMode !== 'specific') setPreloadMode(d);
    }, [preload]);

    useEffect(() => {
        const d = deriveMentionMode(mention);
        if (d !== 'hide' || mentionMode !== 'specific') setMentionMode(d);
    }, [mention]);

    const handlePreloadMode = (next) => {
        setPreloadMode(next);
        if (next === 'all') onPreloadChange(null);
        else if (next === 'none') onPreloadChange([]);
        else onPreloadChange(Array.isArray(preload) ? preload : []);
    };

    const handleMentionMode = (next) => {
        setMentionMode(next);
        if (next === 'hide') onMentionChange(null);
        else if (next === 'all') onMentionChange('all');
        else onMentionChange(Array.isArray(mention) ? mention : []);
    };

    const includeTagSet = new Set(includeTags || []);
    const excludeTagSet = new Set(excludeTags || []);
    const includeSet = new Set(include || []);
    const excludeSet = new Set(exclude || []);

    const hasWhitelist = includeSet.size > 0 || includeTagSet.size > 0;

    const isAccessible = (opt) => {
        const tags = opt.tags || [];
        if (excludeSet.has(opt.value)) return false;
        if (tags.some((t) => excludeTagSet.has(t))) return false;
        if (!hasWhitelist) return true;
        if (includeSet.has(opt.value)) return true;
        if (tags.some((t) => includeTagSet.has(t))) return true;
        return false;
    };

    const visibleOptions = options.filter(isAccessible);

    const preloadAll = preload === null || preload === undefined;
    const preloadNameSet = new Set(
        preloadAll ? visibleOptions.map((o) => o.value) : (Array.isArray(preload) ? preload : [])
    );
    const mentionOptions = visibleOptions.filter((o) => !preloadNameSet.has(o.value));
    const mentionDisabled = preloadAll;

    const preloadModeOpts = [
        { value: 'all',      label: 'All accessible',       desc: `Full name + description for every accessible ${resourceLabel}.` },
        { value: 'none',     label: 'None',                 desc: `Nothing in Tier 1. Mention/count-hint handles the rest.` },
        { value: 'specific', label: `Specific ${resourceLabel}s`, desc: `Only these render with full description + path.` },
    ];

    const mentionModeOpts = [
        { value: 'hide',     label: 'Hide (count hint only)',  desc: `Remaining ${resourceLabel}s shown as "+N more" count.` },
        { value: 'all',      label: 'All remaining',            desc: `List every non-preloaded ${resourceLabel} by name — no count hint.` },
        { value: 'specific', label: `Specific ${resourceLabel}s`, desc: `Only these are listed by name; the rest fall through to "+N more".` },
    ];

    return (
        <div className="pt-3 border-t border-gray-100 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                    <ModeGroup
                        title="Preload — Tier 1 (full)"
                        groupName={`${groupName}_preload`}
                        mode={preloadMode}
                        options={preloadModeOpts}
                        onChange={handlePreloadMode}
                        accent="blue"
                    />
                    {preloadMode === 'specific' && (
                        <div className="pl-2 border-l-2 border-blue-200">
                            <MultiSelect
                                label="Preload names"
                                hint={`Subset of accessible ${resourceLabel}s.`}
                                options={visibleOptions}
                                selected={Array.isArray(preload) ? preload : []}
                                onChange={onPreloadChange}
                                placeholder={`Add ${resourceLabel}s to preload...`}
                                loading={loading}
                            />
                            {Array.isArray(preload) && preload.length === 0 && (
                                <p className="mt-1 text-xs text-amber-600">
                                    No {resourceLabel}s selected. Add at least one, or switch to "None".
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <div className={`space-y-3 ${mentionDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
                    <ModeGroup
                        title="Mention — Tier 2 (name-only)"
                        groupName={`${groupName}_mention`}
                        mode={mentionMode}
                        options={mentionModeOpts}
                        onChange={handleMentionMode}
                        accent="emerald"
                    />
                    {mentionDisabled && (
                        <p className="text-xs text-amber-600">
                            Disabled — every accessible {resourceLabel} is already in Tier 1 (Preload=All).
                            Switch Preload to "None" or "Specific" to enable Mention.
                        </p>
                    )}
                    {!mentionDisabled && mentionMode === 'specific' && (
                        <div className="pl-2 border-l-2 border-emerald-200">
                            <MultiSelect
                                label="Mention names"
                                hint={`Listed by name only. Deduped against preload.`}
                                options={mentionOptions}
                                selected={Array.isArray(mention) ? mention : []}
                                onChange={onMentionChange}
                                placeholder={`Add ${resourceLabel}s to mention...`}
                                loading={loading}
                            />
                        </div>
                    )}
                </div>
            </div>

            <p className="text-xs text-gray-500">
                <strong>Token saver:</strong> Tier 1 full ≈ 80-150 tok / item, Tier 2 name ≈ 5 tok. All
                accessible {resourceLabel}s remain readable via <code className="px-1 py-0.5 bg-gray-100 rounded">{readTool}</code>
                {' '}— agent discovers unlisted via <code className="px-1 py-0.5 bg-gray-100 rounded">ls {listPath}</code>.
            </p>
        </div>
    );
}

const ACCENT_CLASSES = {
    blue: 'border-blue-400 bg-blue-50',
    emerald: 'border-emerald-400 bg-emerald-50',
};

function ModeGroup({ title, groupName, mode, options, onChange, accent = 'blue' }) {
    const activeClass = ACCENT_CLASSES[accent] || ACCENT_CLASSES.blue;
    return (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{title}</label>
            <div className="space-y-2">
                {options.map((opt) => (
                    <label
                        key={opt.value}
                        className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer transition-colors ${
                            mode === opt.value ? activeClass : 'border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                        <input
                            type="radio"
                            name={groupName}
                            value={opt.value}
                            checked={mode === opt.value}
                            onChange={() => onChange(opt.value)}
                            className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900">{opt.label}</div>
                            <div className="text-xs text-gray-500 mt-0.5">{opt.desc}</div>
                        </div>
                    </label>
                ))}
            </div>
        </div>
    );
}
